/**
 * @description EventTracker exposes a window method for the consumers to directly call to subscribe to events.
 * EventTracker checks if the control events are enabled, if not, it throws an error prompting to enable in the config
 * to be able to use the event subscription flows.
 *
 * eg.) Consumer subscribes to the method in the following way
 *
 * 1. Config is updated:
 *
 * {
 *  customPageview?: boolean;
    scrollElement?: string;
    ignoredURLParams?: Array<string>;
    allowedURLParams?: Array<string>;
    pageType?: string;
    impressionTime?: number;
    impressionThreshold?: number;
    sanitizeData?: (data: JSONObject) => JSONObject;

    ...

    eventTrackerConfig: {
        disableDefaultEvents: [...],
        overrideDefaultEvents: { ... },
        controlEvents: ['click']
    }
 * }
 *
 * In some consumer code (Assuming React)
 *
 * useEffect(() => {
 *
 *  if (onSomeChange) {
 *      window.AWSMA.subscribeToEvent('click', document.getElementById('abcd'), (callback: Function) => {
 *          // Callback.
 *
 *          // Handles the callback
 *          // Performs the logic
 *          // Invokes the callback received to pass the data formulated
 *
 *          // It follows this format - https://code.amazon.com/packages/AWSD2CClientPlugins/blobs/8f1452bd74ca65bce0f3cfe429ebe11015faf878/--/src/declarations.d.ts#L29
 *          callback({
 *              eventName: '',
 *              ...
 *          })
 *      })
 *  }
 *
 * }, [onSomeChange])
 *
 * This invokes the EventTrackers method. EventTracker emits a CustomEvent which the concerned plugin will receive.
 *
 * 2. Config is not updated.
 *
 * {
 *  customPageview?: boolean;
    scrollElement?: string;
    ignoredURLParams?: Array<string>;
    allowedURLParams?: Array<string>;
    pageType?: string;
    impressionTime?: number;
    impressionThreshold?: number;
    sanitizeData?: (data: JSONObject) => JSONObject;

    ...

    eventTrackerConfig: {
        disableDefaultEvents: [...],
        overrideDefaultEvents: { ... },
    }
 * }
 *
 *
 * No Control Events have been enabled,
 *
 * Same as above,
 *
 * useEffect(() => {
 *
 *  if (onSomeChange) {
 *      window.AWSMA.subscribeToEvent('click', document.getElementById('abcd'), (callback: Function) => {
 *          // Callback.
 *
 *          // Handles the callback
 *          // Performs the logic
 *          // Invokes the callback received to pass the data formulated
 *
 *          // It follows this format - https://code.amazon.com/packages/AWSD2CClientPlugins/blobs/8f1452bd74ca65bce0f3cfe429ebe11015faf878/--/src/declarations.d.ts#L29
 *          callback({
 *              eventName: '',
 *              ...
 *          })
 *      })
 *  }
 *
 * }, [onSomeChange])
 *
 * This invokes the EventTrackers method. EventTracker throws an Error stating the controlEvent is not enabled in the config.
 *
 * 3. If the consumer tries to subscribe even before the D2C client library is loaded, as we are maintaining a queuing mechanism in the AWSD2CAnalyticsClient,
 * there won't be any errors thrown to the consumers and as the Plugins are loaded, the queued callbacks will be invoked.
 *
 * @class EventTracker
 */
var EventTracker = /** @class */ (function () {
    function EventTracker(hasControlEvents) {
        this.init(hasControlEvents);
    }
    /**
     *
     *
     * @param {JSEventType} eventType
     * @param {HTMLElement} element
     * @param {EventTrackerInternalType['eventCallback']} callback
     * @memberof EventTracker
     */
    EventTracker.prototype.handleControlEventSubscription = function (eventType, element, callback) {
        var eventName = "d2c_".concat(eventType);
        // Dispatch a custom event. This will be listened by the concerned Plugin
        document.dispatchEvent(new CustomEvent(eventName, {
            detail: {
                eventCallback: callback,
                element: element,
                eventType: eventType,
                eventName: eventName,
            },
        }));
    };
    /**
     *
     *
     * @private
     * @param {boolean} hasControlEvents
     * @memberof EventTracker
     */
    EventTracker.prototype.init = function (hasControlEvents) {
        var _this = this;
        // `D2CPluginsSubscribe` customEvent will be triggered from AWSD2CAnalyticsClient upon receiving any subscriptions from consumers.
        document.addEventListener('D2CPluginsSubscribe', function (_a) {
            var _b = _a.detail, eventType = _b.eventType, element = _b.element, eventCallback = _b.eventCallback;
            if (!hasControlEvents) {
                throw new Error('Control Events are not enabled. You need to enable Control Events in the config to subscribe to events.');
            }
            else {
                _this.handleControlEventSubscription(eventType, element, eventCallback);
            }
        });
    };
    return EventTracker;
}());
export default EventTracker;
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiZXZlbnQtdHJhY2tlci5qcyIsInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbIi4uL3NyYy9ldmVudC10cmFja2VyLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQU1BOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7O0dBdUdHO0FBQ0g7SUFDRSxzQkFBWSxnQkFBeUI7UUFDbkMsSUFBSSxDQUFDLElBQUksQ0FBQyxnQkFBZ0IsQ0FBQyxDQUFDO0lBQzlCLENBQUM7SUFFRDs7Ozs7OztPQU9HO0lBQ0gscURBQThCLEdBQTlCLFVBQStCLFNBQXNCLEVBQUUsT0FBb0IsRUFBRSxRQUFtRDtRQUM5SCxJQUFNLFNBQVMsR0FBd0MsY0FBTyxTQUFTLENBQUUsQ0FBQztRQUUxRSx5RUFBeUU7UUFDekUsUUFBUSxDQUFDLGFBQWEsQ0FDcEIsSUFBSSxXQUFXLENBQTJCLFNBQVMsRUFBRTtZQUNuRCxNQUFNLEVBQUU7Z0JBQ04sYUFBYSxFQUFFLFFBQVE7Z0JBQ3ZCLE9BQU8sU0FBQTtnQkFDUCxTQUFTLFdBQUE7Z0JBQ1QsU0FBUyxXQUFBO2FBQ1Y7U0FDRixDQUFDLENBQ0gsQ0FBQztJQUNKLENBQUM7SUFFRDs7Ozs7O09BTUc7SUFDSywyQkFBSSxHQUFaLFVBQWEsZ0JBQXlCO1FBQXRDLGlCQVNDO1FBUkMsa0lBQWtJO1FBQ2xJLFFBQVEsQ0FBQyxnQkFBZ0IsQ0FBQyxxQkFBcUIsRUFBRSxVQUFDLEVBQXdGO2dCQUF0RixjQUE2QyxFQUFuQyxTQUFTLGVBQUEsRUFBRSxPQUFPLGFBQUEsRUFBRSxhQUFhLG1CQUFBO1lBQzdGLElBQUksQ0FBQyxnQkFBZ0IsRUFBRTtnQkFDckIsTUFBTSxJQUFJLEtBQUssQ0FBQyx5R0FBeUcsQ0FBQyxDQUFDO2FBQzVIO2lCQUFNO2dCQUNMLEtBQUksQ0FBQyw4QkFBOEIsQ0FBQyxTQUFTLEVBQUUsT0FBTyxFQUFFLGFBQWEsQ0FBQyxDQUFDO2FBQ3hFO1FBQ0gsQ0FBQyxDQUFDLENBQUM7SUFDTCxDQUFDO0lBQ0gsbUJBQUM7QUFBRCxDQUFDLEFBOUNELElBOENDO0FBRUQsZUFBZSxZQUFZLENBQUMifQ==